import axios from 'axios'
import Token from './Token'

const replace = '%QUERY%'
let query     = `{ ${replace} }`

// GraphQL string literals follow JSON escaping rules. Keep user-entered search
// text out of the query template so a quote in a TO code, material or silo name
// cannot break the request.
const graphQLString = (value) => JSON.stringify(value == null ? '' : String(value))
const graphQLLimit = (value) => {
    let limit = Number(value)

    return Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 20
}

class Query
{
    static withFinishedReceiptOptions(materialId = 0) {
        const id = Number.parseInt(materialId, 10) || 0
        const fields = id > 0
            ? 'packings { id name value } containers { id name capacity max_capacity inventory { id name code } }'
            : 'materials { id name code } packings { id name value }'
        query = query.replace(replace, `finished_receipt_options(material_id: ${id}) { ${fields} } ${replace}`)
        return this
    }

    static withFinishedReceipts() {
        query = query.replace(replace, `finished_receipts(limit: 20) { id code created_at quantity amount reason packing_name packing_value material { id name } container { id name } } ${replace}`)
        return this
    }

    static withMaterials(code = 'all', hasformula = false) {
        query = query.replace(replace, `materials(code: "${code}", hasformula: ${hasformula}) { id name code materialtype { name } } ${replace}`)

        return this
    }

    static withPackings() {
        query = query.replace(replace, `packings { id name value } ${replace}`)

        return this
    }

    static withActions() {
        query = query.replace(replace, `actions { id name } ${replace}`)

        return this
    }

    static withInventoryCover(cover_id) {
        query = query.replace(replace, `list_inventory_covers(cover_id: ${cover_id}) { id solo amount remaining_amount } ${replace}`)

        return this
    }

    static withShifts() {
        query = query.replace(replace, `shifts { id name } ${replace}`)

        return this
    }

    static withShiftUsers() {
        query = query.replace(replace, `shift_users { id shift employees { id name is_checked } } ${replace}`)

        return this
    }

    static withUnits() {
        query = query.replace(replace, `units { id name } ${replace}`)

        return this
    }

    static withCustomers() {
        query = query.replace(replace, `customers { id name total_orders total_liabilities } ${replace}`)

        return this
    }

    static withFields(supplier_id) {
        supplier_id = supplier_id ? supplier_id : 0
        query = query.replace(replace, `fields(supplier_id: ${supplier_id}) { id name } ${replace}`)

        return this
    }

    static withSuppliers() {
        query = query.replace(replace, `suppliers { id name code created_at } ${replace}`)

        return this
    }

    static withContainers(material_id, strict = false) {
        query = query.replace(replace, `containers(material_id: ${material_id}, strict: ${strict}) { id name capacity max_capacity material { id name } } ${replace}`)

        return this
    }

    static withContainersByFormula(material_id) {
        query = query.replace(replace, `containers_by_formula(material_id: ${material_id}) { id name capacity max_capacity inventory { id name code } material { id name } } ${replace}`)

        return this
    }

    static withFormulaDetails(material_id) {

        query = query.replace(replace, `formula_details(material_id: ${material_id}) { id material_id formula_code percent formula_id material { id name materialtype { name } } } ${replace}`)

        return this
    }

    static withCoverCardboard(material_id, amount) {
        query = query.replace(replace, `cover_cardboard(material_id: ${material_id}, amount: ${amount}) { id name } ${replace}`)

        return this
    }

    static withCovers(material_id = null, covertype = 1, amount = null) {
        query = query.replace(replace, `covers(material_id: ${material_id}, covertype: ${covertype}, amount: ${amount}) { id name remaining_amount packing { id name value } } ${replace}`)

        return this
    }

    static withInventories() {
        query = query.replace(replace, `inventories { id name covers { id name amount size materialName } } ${replace}`)

        return this
    }

    static withTos(is_created_suppliment = null) {
        query = query.replace(replace, `tos(is_created_suppliment: ${is_created_suppliment}) { id from_amount to_amount created_at fmaterial { name } tmaterial { name } } ${replace}`)

        return this
    }

    /**
     * Recent machine-run TOs eligible for semi-finished-product disposal.
     * The server deliberately supplies only the TO output silos in
     * `target_containers`; the client must never infer a silo from an MO.
     */
    static withWasteOutTos(search = '', limit = 20) {
        query = query.replace(replace, `waste_out_tos(search: ${graphQLString(search)}, limit: ${graphQLLimit(limit)}) { id code created_at to_amount to_material tmaterial { id name } target_containers { id name capacity material { id name } } } ${replace}`)

        return this
    }

    static withWasteOutMos(search = '', limit = 20, filters = {}) {
        let args = [`search: ${graphQLString(search)}`, `limit: ${graphQLLimit(limit)}`]
        for (let field of ['from_date', 'to_date']) {
            if (filters[field]) args.push(`${field}: ${graphQLString(filters[field])}`)
        }
        query = query.replace(replace, `waste_out_mos(${args.join(', ')}) { id code created_at amount material_id material { id name } target_containers { id name capacity material { id name } } } ${replace}`)

        return this
    }

    static withWasteOutByproducts(search = '', materialId = null, limit = 20) {
        let args = [`search: ${graphQLString(search)}`, `limit: ${graphQLLimit(limit)}`]
        let id = Number(materialId)
        if (Number.isInteger(id) && id > 0) args.push(`material_id: ${id}`)

        query = query.replace(replace, `waste_out_byproducts(${args.join(', ')}) { id code name capacity material { id name } inventory { id name code } } ${replace}`)

        return this
    }

    /**
     * A compact recent-receipt list for the disposal screen. The server owns
     * status and cancellation eligibility, so the UI does not derive either.
     */
    static withWasteOuts(search = '', limit = 20, filters = {}) {
        let args = [
            `search: ${graphQLString(search)}`,
            `limit: ${graphQLLimit(limit)}`,
        ]

        if (filters.from_date) args.push(`from_date: ${graphQLString(filters.from_date)}`)
        if (filters.to_date) args.push(`to_date: ${graphQLString(filters.to_date)}`)
        if (filters.kind) args.push(`kind: ${graphQLString(filters.kind)}`)

        query = query.replace(replace, `waste_outs(${args.join(', ')}) { id code kind kind_label to_id mo_id container_id material_id amount note status status_label can_cancel cancel_note created_at container { id name capacity material { id name } } material { id name } to { id } createdBy { id name } } ${replace}`)

        return this
    }

    static withSos(from_date = null, to_date = null) {
        query = query.replace(replace, `sos(from_date: "${from_date}", to_date: "${to_date}") { status name data { vat for_internal company_name notes export_reason recipient_name samecont shipping_unit id vehicle sort_order created_at estimated_at code customer_id created_by status customer { name } employee { name } } } ${replace}`)

        return this
    }

    static withSosByDate(data = '') {
        query = query.replace(replace, `sos_by_date(data: "${data}") { date data { id code estimated_at exported_at vehicle customer { name } detail { id so_id line_color containername material_id container_id material { name } amount packingdetail { name } packing price } } } ${replace}`)

        return this
    }

    static withSosByCustomer(customer_id, page = 1, limit = 10) {
        query = query.replace(replace, `sos_by_customer(customer_id: ${customer_id}, page: ${page}, limit: ${limit}) { total last_page data { id code total_price vat created_at status estimated_at estimated_at_format exported_at vehicle detail { id so_id line_color containername material_id container_id material { name } amount packingdetail { name } packing price } } } ${replace}`)

        return this
    }

    static withSosAvailable() {
        query = query.replace(replace, `sos_available { id samecont shipping_unit estimated_at estimated_day customer { id name total_liabilities } detail { packingdetail { value } material { name materialtype { name } } round_amount } } ${replace}`)

        return this
    }

    static withSosByGroup() {
        query = query.replace(replace, `sos_by_group { group data { notes estimated_day shipping_unit customer { id name total_liabilities } detail { material { name materialtype { name code } } packing packingdetail { value } round_amount } } } ${replace}`)

        return this
    }

    static withSoDetails(id) {
        query = query.replace(replace, `so_details(so_id: ${id}) { id so_id cover_id_1 cover_id_2 line_color containername material_id container_id material { name } amount packingdetail { name } packing price } ${replace}`)

        return this
    }

    static withMoCode(material_id) {
        query = query.replace(replace, `get_mo_code(material_id: ${material_id}) ${replace}`)

        return this
    }

    static withListChangeViews() {
        query = query.replace(replace, `list_change_views { id name } ${replace}`)

        return this
    }

    static withListDevices() {
        query = query.replace(replace, `list_devices { id name } ${replace}`)

        return this
    }

    static exportSo(so_id) {
        query = query.replace(replace, `export_so(so_id: ${so_id}) ${replace}`)

        return this
    }

    static withContainersByType(params = null) {
        let material_id = params ? (params.material_id || null) : null
        let strict = params ? (params.strict || null) : null
        query = query.replace(replace, `container_by_type { id name containers(material_id: ${material_id}, strict: ${strict}) { id name last_updated updated_at capacity max_capacity containertype_id solo note origin inventory { id name code } material { id name materialtype { color } } } } ${replace}`)

        return this
    }

    static checkPermisstion(all = `
        can_edit_formula_detail,
        unit_management,
        action_management,
        formula_management,
        shiftUsers_management,
        material_management,
        shift_management,
        formula_detail_management,
        inventory_management,
        container_management,
        supplier_management,
        container_type_management,
        input_source_type_management,
        purchase_order_management,
        finished_receipt_management,
        transfer_order_management,
        fields_management,
        field_season_management,
        mos_management,
        sos_management,
        sodetails_management,
        can_edit_formula_detail,
        inventory_covers_management,
        add_new_customer,
        view_list_sos,
        change_capacity,
        container_moving,
        can_change_price_poraw,
        can_change_price_inventory_cover,
        can_change_supplier_inventory_cover,
        waste_out_view,
        waste_out_create,
        waste_out_cancel
    `) {
        query = query.replace(replace, `check_permisstion { ${all} } ${replace}`)

        return this
    }

    static async get() {
        let q = query.replace(replace, '')
        query = `{ ${replace} }`

        return await axios.post('/cmrice', {query: q}, Token())
    }
}

export default Query
